import {
  Add,
  Business,
  Check,
  ChevronRight,
  Close,
  ContentCopy,
  Delete,
  History,
  Language,
  MenuOpen,
  MoreVert,
  Person,
  Public,
  Send,
  SmartToy,
  TrendingDown,
  TrendingUp,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  InputBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";
import { useAssistant } from "src/hooks/useAssistant";
import { showSuccessToast } from "src/utils/Toast/SnackbarProvider";

const components = {
  p: ({ node, ...props }) => (
    <Typography
      variant="body2"
      sx={{
        mb: 1.5,
        "&:last-child": { mb: 0 },
        lineHeight: 1.7,
        fontSize: "0.9rem",
      }}
      {...props}
    />
  ),
  h1: ({ node, ...props }) => (
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, mt: 2.5, mb: 1.5 }}
      {...props}
    />
  ),
  h2: ({ node, ...props }) => (
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 700, mt: 2, mb: 1.2 }}
      {...props}
    />
  ),
  h3: ({ node, ...props }) => (
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 700, mt: 1.5, mb: 0.8 }}
      {...props}
    />
  ),
  ul: ({ node, ...props }) => (
    <Box
      component="ul"
      sx={{ pl: 2.5, mb: 1.5, "& li": { mb: 0.5 } }}
      {...props}
    />
  ),
  ol: ({ node, ...props }) => (
    <Box
      component="ol"
      sx={{ pl: 2.5, mb: 1.5, "& li": { mb: 0.5 } }}
      {...props}
    />
  ),
  li: ({ node, ...props }) => (
    <li style={{ fontSize: "0.875rem", lineHeight: "1.6" }} {...props} />
  ),
  code: ({ node, inline, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || "");
    return !inline ? (
      <Box
        component="pre"
        sx={{
          bgcolor: "#1e1e1e",
          color: "#d4d4d4",
          borderRadius: 2,
          p: 2,
          overflowX: "auto",
          fontSize: "0.8rem",
          mb: 1.5,
          border: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <code className={className} {...props}>
          {children}
        </code>
      </Box>
    ) : (
      <Box
        component="code"
        sx={{
          bgcolor: "rgba(99, 102, 241, 0.1)",
          color: "#6366f1",
          px: 0.8,
          py: 0.3,
          borderRadius: 0.8,
          fontSize: "0.85em",
          fontFamily: "monospace",
          fontWeight: 600,
        }}
        {...props}
      >
        {children}
      </Box>
    );
  },
  table: ({ node, ...props }) => (
    <Box
      sx={{
        overflowX: "auto",
        mb: 2,
        border: "1px solid #e5e7eb",
        borderRadius: 2,
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.85rem",
        }}
        {...props}
      />
    </Box>
  ),
  thead: ({ node, ...props }) => (
    <thead style={{ backgroundColor: "#f9fafb" }} {...props} />
  ),
  th: ({ node, ...props }) => (
    <th
      style={{
        padding: "10px 14px",
        borderBottom: "2px solid #e5e7eb",
        textAlign: "left",
        fontWeight: 600,
        color: "#374151",
      }}
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td
      style={{
        padding: "10px 14px",
        borderBottom: "1px solid #e5e7eb",
        color: "#6b7280",
      }}
      {...props}
    />
  ),
  a: ({ node, children, ...props }) => (
    <a
      style={{
        color: "#6366f1",
        textDecoration: "none",
        fontWeight: 600,
        borderBottom: "1px solid transparent",
        transition: "border-color 0.2s",
      }}
      {...props}
    >
      {children}
    </a>
  ),
  blockquote: ({ node, ...props }) => (
    <Box
      component="blockquote"
      sx={{
        borderLeft: "4px solid #6366f1",
        pl: 2,
        py: 0.5,
        my: 1.5,
        bgcolor: "rgba(99, 102, 241, 0.05)",
        borderRadius: 1,
        color: "#4b5563",
        fontStyle: "italic",
      }}
      {...props}
    />
  ),
};

// Compact Web Search Company Card - Grid Layout with Dynamic Expansion
const WebCompanyCard = ({ company, chatId }) => {
  const { respondToCompany } = useAssistant();
  const details = company.company_details || company;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Initial state from company object
  const isAccepted = company.is_accepted;
  const isDeclined = company.is_declined;

  // Core identification
  const name = details.company_name || "Unknown Company";
  const description =
    details.organization_description || details.description || "";

  // Extracted Fields for Summary
  const website = details.website_url || details.website || "";
  const linkedin = details.linkedin_url || "";

  const handleAction = async (actionType) => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      await respondToCompany({
        chatId,
        companyName: name,
        action: actionType,
        isWeb: true,
        companyData: details,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Logic to find all available fields for the expanded view
  const excludedKeys = [
    "company_name",
    "organization_description",
    "description",
    "company_details",
    "is_company_details_asked",
    "id",
    "created_at",
    "updated_at",
    "structured_results",
    "is_web_result",
    "is_accepted",
    "is_declined",
    "is_outreached",
  ];
  const availableFields = Object.entries(details).filter(([key, value]) => {
    return value && !excludedKeys.includes(key) && typeof value !== "object";
  });

  if (isDeclined) {
    return (
      <Card
        sx={{
          p: 2,
          borderRadius: 3,
          border: "1px solid #f3f4f6",
          background: "#f9fafb",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          opacity: 0.6,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: "#9ca3af",
              textDecoration: "line-through",
            }}
          >
            {name}
          </Typography>
          <Chip
            label="Declined"
            size="small"
            sx={{
              height: 18,
              fontSize: "0.6rem",
              bgcolor: "#f3f4f6",
              color: "#9ca3af",
            }}
          />
        </Box>
        <Button
          size="small"
          onClick={() => handleAction("accept")}
          disabled={isProcessing}
          sx={{
            p: 0,
            minWidth: 0,
            fontSize: "0.7rem",
            color: "#6366f1",
            fontWeight: 600,
          }}
        >
          Restore
        </Button>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        border: "1px solid #e5e7eb",
        background: isAccepted ? "rgba(16, 185, 129, 0.02)" : "#fff",
        boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        position: "relative",
        opacity: isAccepted ? 0.9 : 1,
        "&:hover": {
          boxShadow: "0 12px 24px rgba(0,0,0,0.06)",
          transform: "translateY(-2px)",
          borderColor: isAccepted ? "#10b981" : "#e5e7eb",
        },
        gridColumn: isExpanded
          ? { xs: "span 1", sm: "span 2", md: "span 2", lg: "span 2" }
          : "auto",
      }}
    >
      {isAccepted && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            h: 4,
            bgcolor: "#10b981",
            zIndex: 2,
          }}
        />
      )}
      <Box
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          display: "flex",
          gap: 0.5,
          zIndex: 1,
        }}
      >
        {website && (
          <IconButton
            size="small"
            href={website}
            target="_blank"
            sx={{
              bgcolor: "rgba(16, 185, 129, 0.05)",
              color: "#10b981",
              "&:hover": { bgcolor: "#10b981", color: "#fff" },
            }}
          >
            <Language sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {linkedin && (
          <IconButton
            size="small"
            href={linkedin}
            target="_blank"
            sx={{
              bgcolor: "rgba(0, 119, 181, 0.05)",
              color: "#0077b5",
              "&:hover": { bgcolor: "#0077b5", color: "#fff" },
            }}
          >
            <Public sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{ p: 2.5, flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{ mb: 1.5, pr: 6, display: "flex", alignItems: "center", gap: 1 }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 800,
              color: "#111827",
              fontSize: "0.95rem",
              lineHeight: 1.3,
            }}
          >
            {name}
          </Typography>
          {isAccepted && <Check sx={{ fontSize: 16, color: "#10b981" }} />}
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: "#4b5563",
            fontSize: "0.8rem",
            lineHeight: 1.6,
            display: "-webkit-box",
            WebkitLineClamp: isExpanded ? 6 : 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            mb: 2,
            minHeight: isExpanded ? "auto" : "3.8em",
          }}
        >
          {description}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 0,
            border: "1px solid #f3f4f6",
            borderRadius: 2,
            mb: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{ p: 1, textAlign: "center", borderRight: "1px solid #f3f4f6" }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#9ca3af",
                fontWeight: 700,
                textTransform: "uppercase",
                fontSize: "0.55rem",
                display: "block",
                mb: 0.2,
              }}
            >
              Revenue
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 800, color: "#111827", fontSize: "0.7rem" }}
            >
              {details.revenue || "N/A"}
            </Typography>
          </Box>
          <Box
            sx={{ p: 1, textAlign: "center", borderRight: "1px solid #f3f4f6" }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#9ca3af",
                fontWeight: 700,
                textTransform: "uppercase",
                fontSize: "0.55rem",
                display: "block",
                mb: 0.2,
              }}
            >
              Employees
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 800, color: "#111827", fontSize: "0.7rem" }}
            >
              {details.number_of_employees || "N/A"}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 1,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#9ca3af",
                fontWeight: 700,
                textTransform: "uppercase",
                fontSize: "0.55rem",
                display: "block",
                mb: 0.2,
              }}
            >
              Location
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: "#111827",
                fontSize: "0.65rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {details.country || details.location || "N/A"}
            </Typography>
          </Box>
        </Box>

        {isExpanded && (
          <Box
            sx={{
              mt: 1,
              pt: 2,
              borderTop: "1px dashed #e5e7eb",
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2,
              mb: 2,
            }}
          >
            {availableFields.map(([key, value]) => (
              <Box key={key} sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#9ca3af",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    fontSize: "0.6rem",
                    letterSpacing: "0.5px",
                    mb: 0.3,
                  }}
                >
                  {key.replace(/_/g, " ")}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#1f2937",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    wordBreak: "break-word",
                  }}
                >
                  {value.toString()}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            mt: "auto",
            pt: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              p: 0,
              minWidth: 0,
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "#6366f1",
              "&:hover": { background: "none", textDecoration: "underline" },
            }}
          >
            {isExpanded ? "Close details" : "Read more"}
          </Button>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              onClick={() => handleAction("decline")}
              disabled={isProcessing || isAccepted}
              sx={{
                minWidth: 0,
                p: "4px 12px",
                borderRadius: 1.5,
                textTransform: "none",
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#9ca3af",
                "&:hover": { color: "#ef4444", bgcolor: "#fef2f2" },
              }}
            >
              Decline
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => handleAction("accept")}
              disabled={isProcessing || isAccepted}
              sx={{
                height: 30,
                borderRadius: 1.5,
                textTransform: "none",
                fontSize: "0.7rem",
                fontWeight: 700,
                bgcolor: isAccepted ? "#10b981" : "#10b981",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#059669",
                  boxShadow: "0 4px 12px rgba(16, 185, 129, 0.2)",
                },
                "&.Mui-disabled": {
                  bgcolor: isAccepted ? "#10b981" : "#f3f4f6",
                  color: isAccepted ? "#fff" : "#9ca3af",
                },
              }}
            >
              {isAccepted ? "Accepted" : "Accept"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

const DatabaseCompanyCard = ({
  company,
  chatId,
  variant = "rich",
  showActions = true,
  minimal = false,
}) => {
  const navigate = useNavigate();
  const { respondToCompany } = useAssistant();
  const [isProcessing, setIsProcessing] = useState(false);

  const details = company.company_details || company;
  const risks = company.risk_factors || [];
  const advantages = company.competitive_advantages || [];
  const tech = details.technology_stack || [];

  const isAccepted = company.is_accepted;
  const isDeclined = company.is_declined;

  // `minimal` is the legacy prop; treat it as compact for backwards compat.
  const effectiveVariant = variant === "compact" || minimal ? "compact" : "rich";

  const companyId =
    company.id ||
    company.company_id ||
    details.id ||
    details.company_id ||
    null;
  const canOpen = Boolean(companyId);

  const handleOpen = () => {
    if (!canOpen) return;
    navigate(`/companies/${companyId}`, {
      state: { fromPage: "assistant" },
    });
  };

  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const handleAction = async (actionType) => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      await respondToCompany({
        chatId,
        companyName: details.company_name,
        action: actionType,
        isWeb: false,
        companyData: details,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Compact row card: avatar + name + industry chip + 1-line desc + chevron.
  // Used for the "rest" of internal_details results, and any caller that
  // passes variant="compact" (or the legacy minimal=true).
  if (effectiveVariant === "compact") {
    const initials = (details.company_name || "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0])
      .join("")
      .toUpperCase();
    const industry =
      details.industry_label ||
      details.industry_primary ||
      details.industry ||
      details.industries ||
      null;
    const location =
      details.headquarters_location ||
      details.country_label ||
      details.country ||
      details.location ||
      null;
    const description =
      details.business_description ||
      details.organization_description ||
      details.pitch_line ||
      details.description ||
      "";

    return (
      <Card
        onClick={canOpen ? handleOpen : undefined}
        sx={{
          borderRadius: 2.5,
          border: "1px solid #eef0f4",
          background: "#fff",
          boxShadow: "none",
          cursor: canOpen ? "pointer" : "default",
          transition: "all 0.18s ease",
          "&:hover": canOpen
            ? {
                borderColor: "#6366f1",
                boxShadow: "0 6px 18px rgba(99, 102, 241, 0.10)",
                "& .compact-chevron": {
                  color: "#6366f1",
                  transform: "translateX(3px)",
                },
              }
            : {},
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 1.75,
            py: 1.25,
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: "rgba(99, 102, 241, 0.12)",
              color: "#6366f1",
              fontSize: "0.75rem",
              fontWeight: 800,
            }}
          >
            {initials || <Business sx={{ fontSize: 18 }} />}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  color: "#111827",
                  fontSize: "0.85rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {details.company_name || "Unnamed company"}
              </Typography>
              {industry && (
                <Chip
                  label={industry}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.65rem",
                    bgcolor: "rgba(99, 102, 241, 0.08)",
                    color: "#4f46e5",
                    fontWeight: 700,
                    border: "1px solid rgba(99, 102, 241, 0.18)",
                  }}
                />
              )}
            </Box>
            {(description || location) && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "#6b7280",
                  fontSize: "0.72rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  mt: 0.25,
                }}
              >
                {[location, description].filter(Boolean).join(" · ")}
              </Typography>
            )}
          </Box>

          {canOpen && (
            <ChevronRight
              className="compact-chevron"
              sx={{
                fontSize: 20,
                color: "#9ca3af",
                transition: "all 0.18s ease",
                flexShrink: 0,
              }}
            />
          )}
        </Box>
      </Card>
    );
  }

  if (showActions && isDeclined) {
    return (
      <Card
        sx={{
          p: 2.5,
          borderRadius: 4,
          border: "1px solid #f3f4f6",
          background: "#f9fafb",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          opacity: 0.6,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{ p: 1, borderRadius: 2, bgcolor: "#f3f4f6", color: "#9ca3af" }}
          >
            <Business sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: "#9ca3af",
                textDecoration: "line-through",
              }}
            >
              {details.company_name}
            </Typography>
            <Typography variant="caption" sx={{ color: "#9ca3af" }}>
              Company declined from target list
            </Typography>
          </Box>
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleAction("accept")}
          disabled={isProcessing}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.75rem",
            fontWeight: 700,
            borderColor: "#e5e7eb",
            color: "#6366f1",
            "&:hover": {
              borderColor: "#6366f1",
              bgcolor: "rgba(99, 102, 241, 0.05)",
            },
          }}
        >
          Restore to Database
        </Button>
      </Card>
    );
  }

  const formatCurrency = (val) => {
    if (!val) return "N/A";
    const num = parseFloat(val.toString().replace(/[^0-9.]/g, ""));
    if (isNaN(num)) return val;
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
    return `$${num}`;
  };

  return (
    <Card
      onClick={canOpen ? handleOpen : undefined}
      sx={{
        borderRadius: 4,
        border: "1px solid #e5e7eb",
        background: "#fff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        cursor: canOpen ? "pointer" : "default",
        "&:hover": {
          boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
          transform: canOpen ? "translateY(-4px)" : "none",
          borderColor: "#6366f1",
          "& .rich-chevron": {
            color: "#6366f1",
            transform: "translateX(3px)",
          },
        },
      }}
    >
      <Box
        sx={{
          p: 2.5,
          background:
            "linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, #ffffff 100%)",
          borderBottom: "1px solid #f3f4f6",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: "#111827",
                fontSize: "1.1rem",
                mb: 0.5,
              }}
            >
              {details.company_name}
            </Typography>
            {(details.website || details.website_url) && (
              <Button
                startIcon={<Language sx={{ fontSize: 16 }} />}
                href={details.website || details.website_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={stopPropagation}
                size="small"
                sx={{
                  textTransform: "none",
                  color: "#6b7280",
                  fontSize: "0.75rem",
                  p: 0,
                  minWidth: 0,
                  "&:hover": { background: "none", color: "#6366f1" },
                }}
              >
                {(details.website || details.website_url).replace(
                  /^https?:\/\/(www\.)?/,
                  "",
                )}
              </Button>
            )}
          </Box>
          {canOpen && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#9ca3af",
                fontSize: "0.7rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                flexShrink: 0,
                mt: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  color: "inherit",
                }}
              >
                Open
              </Typography>
              <ChevronRight
                className="rich-chevron"
                sx={{
                  fontSize: 18,
                  color: "inherit",
                  transition: "all 0.2s ease",
                }}
              />
            </Box>
          )}
        </Box>

        <Typography
          variant="body2"
          sx={{ color: "#4b5563", lineHeight: 1.6, fontSize: "0.85rem" }}
        >
          {details.organization_description || details.description}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 0,
          borderBottom: "1px solid #f3f4f6",
        }}
      >
        <Box
          sx={{ p: 2, textAlign: "center", borderRight: "1px solid #f3f4f6" }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#9ca3af",
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "0.6rem",
              display: "block",
              mb: 0.5,
            }}
          >
            Revenue
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: "#111827" }}
          >
            {formatCurrency(details.organization_revenue || details.revenue)}
          </Typography>
        </Box>
        <Box
          sx={{ p: 2, textAlign: "center", borderRight: "1px solid #f3f4f6" }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#9ca3af",
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "0.6rem",
              display: "block",
              mb: 0.5,
            }}
          >
            Employees
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: "#111827" }}
          >
            {details.estimated_employee_count ||
              details.number_of_employees ||
              "N/A"}
          </Typography>
        </Box>
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography
            variant="caption"
            sx={{
              color: "#9ca3af",
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "0.6rem",
              display: "block",
              mb: 0.5,
            }}
          >
            Location
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              color: "#111827",
              fontSize: "0.75rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {details.headquarters_location || details.location || "N/A"}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Stack spacing={2.5}>
          {advantages.length > 0 && (
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <TrendingUp sx={{ fontSize: 16, color: "#10b981" }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "#111827",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Key Advantages
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {advantages.map((adv, i) => (
                  <Chip
                    key={i}
                    label={adv}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      bgcolor: "rgba(16, 185, 129, 0.08)",
                      color: "#059669",
                      fontWeight: 600,
                      border: "1px solid rgba(16, 185, 129, 0.2)",
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {risks.length > 0 && (
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <TrendingDown sx={{ fontSize: 16, color: "#ef4444" }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "#111827",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Risk Factors
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {risks.map((risk, i) => (
                  <Chip
                    key={i}
                    label={risk}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      bgcolor: "rgba(239, 68, 68, 0.08)",
                      color: "#dc2626",
                      fontWeight: 600,
                      border: "1px solid rgba(239, 68, 68, 0.2)",
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {tech.length > 0 && (
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "4px",
                    bgcolor: "#6366f1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{ color: "#fff", fontSize: "9px", fontWeight: 900 }}
                  >
                    T
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "#111827",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Tech Stack
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                {tech.map((t, i) => (
                  <Chip
                    key={i}
                    label={t}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      color: "#6b7280",
                      borderColor: "#e5e7eb",
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      </Box>
    </Card>
  );
};

export default function AssistantDrawer() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [inputValue, setInputValue] = useState("");
  const [isHistoryOpen, setIsHistoryOpen] = useState(!isMobile);
  const [sessionMenuAnchor, setSessionMenuAnchor] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [drawerWidth, setDrawerWidth] = useState(isMobile ? 100 : 75);
  const [mode, setMode] = useState("internal_details");
  const [webSearch, setWebSearch] = useState(false);

  // Markdown components map with an `a` renderer that uses React Router
  // for internal links (e.g. `/companies/123`) so company-name mentions in
  // the assistant prose open inside the SPA instead of doing a full reload.
  // External links keep the default new-tab behavior.
  const markdownComponents = {
    ...components,
    a: ({ node, children, href, ...props }) => {
      const isInternal = typeof href === "string" && href.startsWith("/");
      return (
        <a
          href={href}
          onClick={(e) => {
            if (isInternal) {
              e.preventDefault();
              navigate(href);
            }
          }}
          target={isInternal ? undefined : "_blank"}
          rel={isInternal ? undefined : "noopener noreferrer"}
          style={{
            color: "#6366f1",
            textDecoration: "none",
            fontWeight: 700,
            borderBottom: "1px solid rgba(99, 102, 241, 0.3)",
            transition: "border-color 0.2s",
          }}
          {...props}
        >
          {children}
        </a>
      );
    },
  };

  const isResizing = useRef(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const shouldScrollRef = useRef(true);
  const inputRef = useRef(null);

  const {
    isOpen,
    toggleAssistant,
    isLoadingSessions,
    isFetchingMoreSessions,
    chatSessions,
    hasMoreSessions,
    loadMoreSessions,
    currentChatId,
    selectChatSession,
    createNewChat,
    activeMessages,
    isLoadingMessages,
    isFetchingMoreMessages,
    loadMoreMessages,
    sendMessage,
    isStreaming,
    loadingStage,
    deleteChatSession,
    refreshSessions,
    hasMoreMessages,
    deleteMessage,
  } = useAssistant();

  const scrollToBottom = useCallback((behavior = "smooth") => {
    if (shouldScrollRef.current && messagesContainerRef.current) {
      const { scrollHeight, clientHeight } = messagesContainerRef.current;
      const maxScrollTop = scrollHeight - clientHeight;

      if (behavior === "auto") {
        messagesContainerRef.current.scrollTop = maxScrollTop;
      } else {
        messagesContainerRef.current.scrollTo({
          top: maxScrollTop,
          behavior: "smooth",
        });
      }
    }
  }, []);

  useEffect(() => {
    if (activeMessages.length > 0) {
      const lastMessage = activeMessages[activeMessages.length - 1];
      if (lastMessage.isStreaming || lastMessage.isTemp) {
        scrollToBottom("smooth");
      }
    }
  }, [activeMessages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && currentChatId) {
      shouldScrollRef.current = true;
      setTimeout(() => scrollToBottom("auto"), 100);
    }
  }, [currentChatId, isOpen, scrollToBottom]);

  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } =
      messagesContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    shouldScrollRef.current = isNearBottom;
  }, []);

  useEffect(() => {
    setIsHistoryOpen(!isMobile);
    if (isMobile) {
      setDrawerWidth(100);
    } else {
      setDrawerWidth(75);
    }
  }, [isMobile]);

  useEffect(() => {
    if (mode !== "not_outreached") {
      setWebSearch(false);
    }
  }, [mode]);

  const startResizing = (e) => {
    isResizing.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", stopResizing);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const handleMouseMove = (e) => {
    if (!isResizing.current) return;
    const newWidthPx = window.innerWidth - e.clientX;
    const newWidthPercent = (newWidthPx / window.innerWidth) * 100;
    if (newWidthPercent > 40 && newWidthPercent < 90) {
      setDrawerWidth(newWidthPercent);
    }
  };

  const stopResizing = () => {
    isResizing.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "default";
    document.body.style.userSelect = "";
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isStreaming) return;

    const userText = inputValue;
    setInputValue("");
    shouldScrollRef.current = true;

    try {
      await sendMessage(userText, mode, webSearch);
    } catch (error) {
      console.error("Message send failed", error);
      showSuccessToast(error.message || "Failed to send message", "error");
    }

    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleNewChat = () => {
    createNewChat();
    setInputValue("");
    if (isMobile) setIsHistoryOpen(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSessionMenuOpen = (event, sessionId) => {
    event.stopPropagation();
    setSessionMenuAnchor(event.currentTarget);
    setSelectedSessionId(sessionId);
  };

  const handleSessionMenuClose = () => {
    setSessionMenuAnchor(null);
    setSelectedSessionId(null);
  };

  const handleDeleteSession = () => {
    if (selectedSessionId) {
      deleteChatSession(selectedSessionId);
    }
    handleSessionMenuClose();
  };

  const formatTime = (dateString, full = false) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";

      if (full)
        return date.toLocaleString([], {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });

      const now = new Date();
      const isToday =
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();

      if (isToday) {
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      } else {
        return date.toLocaleDateString([], { month: "short", day: "numeric" });
      }
    } catch (e) {
      return "";
    }
  };

  const truncate = (str, len) => {
    if (!str) return "";
    if (str.length <= len) return str;
    return str.substring(0, len) + "...";
  };

  const ChatMessage = ({ chat, isLatest }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuType, setMenuType] = useState(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event, type) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setMenuType(type);
    };

    const handleClose = () => {
      setAnchorEl(null);
      setMenuType(null);
    };

    const handleCopy = () => {
      const text =
        menuType === "user" ? chat.user_prompt : chat.llm_response || "";
      navigator.clipboard.writeText(text || "");
      showSuccessToast("Copied to clipboard");
      handleClose();
    };

    const handleDelete = () => {
      deleteMessage(chat.chat_id || currentChatId, chat.id);
      handleClose();
    };

    return (
      <Box key={chat.id}>
        {chat.user_prompt && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mb: 3,
              opacity: chat.isTemp && !chat.llm_response ? 0.7 : 1,
              position: "relative",
              "&:hover .message-actions": { opacity: 1, transform: "scale(1)" },
            }}
          >
            {!chat.isTemp && (
              <Box
                className="message-actions"
                sx={{
                  opacity: isMobile ? 1 : 0,
                  transform: isMobile ? "scale(1)" : "scale(0.9)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  display: "flex",
                  alignItems: "flex-start",
                  mr: 0.5,
                  mt: 0.5,
                }}
              >
                <IconButton
                  size="small"
                  onClick={(e) => handleOpen(e, "user")}
                  sx={{
                    color: "#9ca3af",
                    bgcolor: "rgba(255,255,255,0.9)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "#fff",
                      color: "#6366f1",
                      boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <MoreVert sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            )}
            <Box
              sx={{
                maxWidth: { xs: "70%", sm: "75%", md: "80%" },
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  p: 2,
                  px: 2.5,
                  borderRadius: "16px 16px 4px 16px",
                  background: chat.isError
                    ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                    : "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                  color: "#fff",
                  boxShadow: chat.isError
                    ? "0 4px 16px rgba(239, 68, 68, 0.3)"
                    : "0 4px 16px rgba(99, 102, 241, 0.3)",
                  position: "relative",
                  transition: "all 0.2s",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  "&:hover": {
                    boxShadow: chat.isError
                      ? "0 6px 20px rgba(239, 68, 68, 0.4)"
                      : "0 6px 20px rgba(99, 102, 241, 0.4)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <ReactMarkdown components={markdownComponents}>
                  {chat.user_prompt}
                </ReactMarkdown>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  px: 1,
                  color: chat.isError ? theme.palette.error.main : "#9ca3af",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.75,
                  fontSize: "0.7rem",
                  fontWeight: 500,
                }}
              >
                {formatTime(chat.created_at)}
                {chat.isError && " • Failed"}
              </Typography>
            </Box>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                background: "linear-gradient(135deg, #ec4899 0%, #d946ef 100%)",
                mt: 0.5,
                boxShadow: "0 4px 12px rgba(236, 72, 153, 0.3)",
                border: "2px solid #fff",
              }}
            >
              <Person sx={{ fontSize: 16 }} />
            </Avatar>
          </Box>
        )}

        {(chat.llm_response ||
          chat.isTemp ||
          chat.isError ||
          chat.web_results?.length > 0 ||
          chat.searched_results?.length > 0 ||
          chat.structured_results?.length > 0) &&
          chat.user_prompt && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                gap: 1,
                mb: 3,
                position: "relative",
                width: "100%",
                minWidth: 0,
                "&:hover .message-actions": {
                  opacity: 1,
                  transform: "scale(1)",
                },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background:
                    "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                  mt: 0.5,
                  boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
                  border: "2px solid #fff",
                }}
              >
                <SmartToy sx={{ fontSize: 16 }} />
              </Avatar>
              <Box
                sx={{
                  flex: "0 1 auto",
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: "16px 16px 16px 4px",
                    bgcolor: "#fff",
                    color: chat.isError ? theme.palette.error.main : "#1f2937",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                    border: "1px solid #f3f4f6",
                    transition: "all 0.2s",
                    overflowX: "auto",
                    overflowY: "visible",
                    width: "fit-content",
                    maxWidth: "100%",
                    "&:hover": {
                      boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                      transform: "translateY(-1px)",
                    },
                    "& > *": { maxWidth: "100%" },
                    "& p, & h1, & h2, & h3, & h4, & h5, & h6, & ul, & ol, & blockquote":
                      {
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                      },
                    "& pre": { overflowX: "auto", maxWidth: "100%" },
                    "& table": {
                      display: "table",
                      width: "auto",
                      minWidth: "max-content",
                      borderCollapse: "collapse",
                    },
                    "&::-webkit-scrollbar": { height: "8px" },
                    "&::-webkit-scrollbar-thumb": {
                      bgcolor: "#d1d5db",
                      borderRadius: "10px",
                      "&:hover": { bgcolor: "#9ca3af" },
                    },
                    "&::-webkit-scrollbar-track": {
                      bgcolor: "#f3f4f6",
                      borderRadius: "10px",
                    },
                  }}
                >
                  {renderAssistantContent(chat)}
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mt: 0.75, px: 1 }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#9ca3af",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontSize: "0.7rem",
                      fontWeight: 500,
                    }}
                  >
                    {chat.isStreaming && (
                      <CircularProgress
                        size={8}
                        thickness={6}
                        sx={{ mr: 0.5, color: "#6366f1" }}
                      />
                    )}
                    {formatTime(chat.modified || chat.created_at)}
                  </Typography>
                  {chat.isError && (
                    <Chip
                      label="Error"
                      size="small"
                      color="error"
                      sx={{ height: 18, fontSize: "0.65rem", fontWeight: 600 }}
                    />
                  )}
                </Stack>
              </Box>
              {!chat.isTemp && !chat.isStreaming && (
                <Box
                  className="message-actions"
                  sx={{
                    opacity: isMobile ? 1 : 0,
                    transform: isMobile ? "scale(1)" : "scale(0.9)",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    display: "flex",
                    alignItems: "flex-start",
                    ml: 0.5,
                    mt: 0.5,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => handleOpen(e, "bot")}
                    sx={{
                      color: "#9ca3af",
                      bgcolor: "rgba(255,255,255,0.9)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      "&:hover": {
                        bgcolor: "#fff",
                        color: "#6366f1",
                        boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)",
                      },
                      transition: "all 0.2s",
                    }}
                  >
                    <MoreVert sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: menuType === "user" ? "right" : "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: menuType === "user" ? "right" : "left",
          }}
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 2.5,
              mt: 1,
              border: "1px solid #e5e7eb",
              boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
            },
          }}
        >
          <MenuItem
            onClick={handleCopy}
            sx={{
              fontSize: "0.85rem",
              py: 1.5,
              px: 2,
              fontWeight: 500,
              "&:hover": { bgcolor: "rgba(99, 102, 241, 0.08)" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <ContentCopy
                fontSize="small"
                sx={{ fontSize: 18, color: "#6366f1" }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Copy"
              primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
            />
          </MenuItem>
          <MenuItem
            onClick={handleDelete}
            sx={{
              fontSize: "0.85rem",
              color: "#ef4444",
              py: 1.5,
              px: 2,
              fontWeight: 500,
              "&:hover": { bgcolor: "rgba(239, 68, 68, 0.08)" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Delete
                fontSize="small"
                sx={{ fontSize: 18, color: "#ef4444" }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Delete"
              primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
            />
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  const renderStreamingResponse = (chat) => {
    let content = chat.llm_response;

    // Extract text content if llm_response is a JSON object (as stored in DB)
    if (typeof content === "object" && content !== null) {
      content = content.llm_response || "";
    }

    if (!content && chat.isStreaming) {
      const stageLabels = {
        searching_internal: "Searching internal companies...",
        calling_web: "Fetching external data...",
        enriching: "Enriching company data...",
      };
      const stageLabel = loadingStage ? stageLabels[loadingStage] : null;
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={12} sx={{ color: "#6366f1" }} />
          <Typography
            sx={{ color: "#9ca3af", fontStyle: "italic", fontSize: "0.85rem" }}
          >
            {stageLabel || "Thinking..."}
          </Typography>
        </Box>
      );
    }

    if (!content) return null;

    return (
      <>
        <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
          {content}
        </ReactMarkdown>
      </>
    );
  };

  const renderStructuredResponse = (chat, isCompact = false) => {
    // Handle both raw pipeline results and normalized database results
    const rawWeb = chat.web_results || chat.web_searched_comanies || [];
    const rawDb = chat.searched_results || chat.searched_result_companies || [];

    // Results from the new normalized table
    const normalizedWeb = (chat.structured_results || []).filter(
      (r) => r.is_web_result,
    );
    const normalizedDb = (chat.structured_results || []).filter(
      (r) => !r.is_web_result,
    );

    // Deduplicate based on company_name to prevent double-rendering
    const webResultsMap = new Map();
    [...rawWeb, ...normalizedWeb].forEach((r) => {
      const name = r.company_name || r.company_details?.company_name;
      if (name && !webResultsMap.has(name)) webResultsMap.set(name, r);
    });
    const webResults = Array.from(webResultsMap.values());

    const searchedResultsMap = new Map();
    [...rawDb, ...normalizedDb].forEach((r) => {
      const name = r.company_name || r.company_details?.company_name;
      if (name && !searchedResultsMap.has(name))
        searchedResultsMap.set(name, r);
    });
    const searchedResults = Array.from(searchedResultsMap.values());

    // In internal_details mode, narrow to the LLM-cited subset if the
    // backend supplied featured_company_ids — the answer only discusses a
    // few of the retrieved candidates, so showing all of them as cards is
    // noisy. Outside internal_details we keep the full list.
    const messageMode = chat.mode || mode;
    const featuredIds = Array.isArray(chat.featured_company_ids)
      ? chat.featured_company_ids
      : chat.final_llm_response?.featured_company_ids || [];
    let displayedResults = searchedResults;
    if (messageMode === "internal_details" && featuredIds.length > 0) {
      const byId = new Map();
      searchedResults.forEach((r) => {
        const id = r.id ?? r.company_id ?? r.company_details?.id;
        if (id != null) byId.set(id, r);
      });
      const ordered = featuredIds.map((id) => byId.get(id)).filter(Boolean);
      if (ordered.length > 0) displayedResults = ordered;
    }

    if (!webResults.length && !searchedResults.length) return null;

    return (
      <Box sx={{ width: "100%", mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            width: "100%",
          }}
        >
          {searchedResults.length > 0 && (
            <Box>
              {!isCompact && chat.search_results_description && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4b5563",
                    mb: 2,
                    lineHeight: 1.6,
                    bgcolor: "#f9fafb",
                    p: 1.5,
                    borderRadius: 2,
                    borderLeft: "4px solid #6366f1",
                  }}
                >
                  {chat.search_results_description}
                </Typography>
              )}

              {!isCompact && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: "rgba(99, 102, 241, 0.1)",
                      color: "#6366f1",
                      display: "flex",
                    }}
                  >
                    <Business sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 900,
                        color: "#111827",
                        fontSize: "0.9rem",
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                      }}
                    >
                      {(chat.mode || mode) === "internal_details"
                        ? "Internal Companies"
                        : "Target Database"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#6b7280", fontWeight: 600 }}
                    >
                      {messageMode === "internal_details"
                        ? `${displayedResults.length} relevant ${
                            displayedResults.length === 1
                              ? "company"
                              : "companies"
                          } · click any card to open`
                        : `Found ${searchedResults.length} matches in specialized market data`}
                    </Typography>
                  </Box>
                </Box>
              )}
              {(() => {
                if (!isCompact && messageMode === "internal_details") {
                  // Hybrid layout: top 2 rich, rest compact, no actions.
                  // displayedResults was narrowed to the LLM-cited subset
                  // earlier in this function when featured_company_ids was
                  // provided.
                  const topRich = displayedResults.slice(0, 2);
                  const restCompact = displayedResults.slice(2);
                  return (
                    <Stack spacing={2}>
                      {topRich.map((comp, idx) => (
                        <DatabaseCompanyCard
                          key={`search-rich-${idx}`}
                          company={comp}
                          chatId={chat.chat_id}
                          variant="rich"
                          showActions={false}
                        />
                      ))}
                      {restCompact.length > 0 && (
                        <Stack spacing={1} sx={{ mt: topRich.length ? 1 : 0 }}>
                          {restCompact.map((comp, idx) => (
                            <DatabaseCompanyCard
                              key={`search-compact-${idx}`}
                              company={comp}
                              chatId={chat.chat_id}
                              variant="compact"
                              showActions={false}
                            />
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  );
                }
                return (
                  <Stack spacing={isCompact ? 1 : 3}>
                    {searchedResults.map((comp, idx) => (
                      <DatabaseCompanyCard
                        key={`search-${idx}`}
                        company={comp}
                        chatId={chat.chat_id}
                        minimal={isCompact}
                      />
                    ))}
                  </Stack>
                );
              })()}
            </Box>
          )}

          {webResults.length > 0 && (
            <Box>
              {chat.web_search_description && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4b5563",
                    mb: 2,
                    lineHeight: 1.6,
                    bgcolor: "#f0fdf4",
                    p: 1.5,
                    borderRadius: 2,
                    borderLeft: "4px solid #10b981",
                  }}
                >
                  {chat.web_search_description}
                </Typography>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: "rgba(16, 185, 129, 0.1)",
                    color: "#10b981",
                    display: "flex",
                  }}
                >
                  <Public sx={{ fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 900,
                      color: "#111827",
                      fontSize: "0.9rem",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    Web Intelligence
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#6b7280", fontWeight: 600 }}
                  >
                    Found {webResults.length} relevant matches from live web
                    search
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                    xl: "repeat(4, 1fr)",
                  },
                  gap: 2.5,
                }}
              >
                {webResults.map((comp, idx) => (
                  <WebCompanyCard
                    key={`web-${idx}`}
                    company={comp}
                    chatId={chat.chat_id}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderAssistantContent = (chat) => {
    if (chat.isError) {
      return (
        <Typography sx={{ color: "#ef4444", fontWeight: 500 }}>
          Failed to generate response
        </Typography>
      );
    }

    // Detect if this is an "Outreached" chat (UserChats usually comes with llm_response as object from backend, or we can check other flags if available)
    // Ideally we should have a flag, but based on recent changes, UserChats (Outreached) have llm_response as object in DB but string in UI?
    // Actually, let's rely on the fact that for Outreached, we want minimal cards.
    // Let's assume if it is NOT a 'structured' category (which is for Stage 1), it is Outreached.
    const isOutreached = chat.category !== "structured";

    const hasStructuredData =
      chat.searched_results?.length > 0 ||
      chat.searched_result_companies?.length > 0 ||
      chat.web_results?.length > 0 ||
      chat.web_searched_comanies?.length > 0;

    return (
      <Box sx={{ width: "100%" }}>
        {(chat.llm_response ||
          (chat.isStreaming && chat.category !== "structured")) && (
          <Box sx={{ mb: hasStructuredData ? 0.5 : 0 }}>
            {renderStreamingResponse(chat)}
          </Box>
        )}

        {(chat.category === "structured" || hasStructuredData) &&
          renderStructuredResponse(chat, isOutreached)}

        {!chat.llm_response &&
          chat.isStreaming &&
          chat.category === "structured" &&
          !hasStructuredData && (
            <Typography sx={{ color: "#9ca3af", fontStyle: "italic" }}>
              Fetching structured data...
            </Typography>
          )}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={toggleAssistant}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : `${drawerWidth}%`,
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f8f9fa",
          overflow: "hidden",
        },
      }}
    >
      {!isMobile && (
        <Box
          onMouseDown={startResizing}
          sx={{
            position: "absolute",
            left: -4,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: "col-resize",
            zIndex: 1300,
            "&:hover": {
              bgcolor: "primary.main",
              opacity: 0.5,
            },
            transition: "background-color 0.2s",
          }}
        />
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e5e7eb",
          zIndex: 10,
          background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 } }}
        >
          <IconButton
            onClick={() => {
              setIsHistoryOpen(!isHistoryOpen);
              refreshSessions();
            }}
            size="small"
            sx={{
              bgcolor: isHistoryOpen ? "rgba(99, 102, 241, 0.1)" : "#f3f4f6",
              color: isHistoryOpen ? "#6366f1" : "#6b7280",
              "&:hover": {
                bgcolor: isHistoryOpen ? "rgba(99, 102, 241, 0.2)" : "#e5e7eb",
                transform: "scale(1.05)",
              },
              transition: "all 0.2s",
            }}
          >
            {isHistoryOpen ? (
              <MenuOpen fontSize="small" />
            ) : (
              <History fontSize="small" />
            )}
          </IconButton>
          <ImageTitle />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={handleNewChat}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 1.5,
              bgcolor: "#f3f4f6",
              color: "#6366f1",
              fontWeight: 700,
              fontSize: "0.75rem",
              "&:hover": {
                bgcolor: "#e5e7eb",
                transform: "scale(1.02)",
              },
              transition: "all 0.2s",
            }}
          >
            New Chat
          </Button>
          <IconButton
            onClick={toggleAssistant}
            size="small"
            sx={{
              bgcolor: "#f3f4f6",
              color: "#6b7280",
              "&:hover": {
                bgcolor: "#fee2e2",
                color: "#ef4444",
                transform: "scale(1.05)",
              },
              transition: "all 0.2s",
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      <Box
        sx={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: isHistoryOpen ? (isMobile ? "80%" : "320px") : 0,
            minWidth: 0,
            opacity: isHistoryOpen ? 1 : 0,
            visibility: isHistoryOpen ? "visible" : "hidden",
            borderRight: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: "column",
            background: "#fff",
            flexShrink: 0,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            zIndex: 20,
            boxShadow: isHistoryOpen ? "4px 0 24px rgba(0,0,0,0.12)" : "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2.5,
              pb: 2,
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
            }}
            onClick={refreshSessions}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
                }}
              >
                <History sx={{ fontSize: 18, color: "#fff" }} />
              </Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "#1f2937",
                  letterSpacing: "0.3px",
                  fontSize: "0.85rem",
                }}
              >
                CONVERSATIONS
              </Typography>
            </Box>
            {isMobile && (
              <IconButton
                size="small"
                onClick={() => setIsHistoryOpen(false)}
                sx={{ color: "#6b7280" }}
              >
                <Close fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#d1d5db",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            }}
          >
            {isLoadingSessions ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={32} sx={{ color: "#6366f1" }} />
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {chatSessions.map((session) => (
                  <Card
                    key={session.chat_id}
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      border:
                        currentChatId === session.chat_id
                          ? "2px solid #6366f1"
                          : "1px solid #e5e7eb",
                      bgcolor:
                        currentChatId === session.chat_id
                          ? "rgba(99, 102, 241, 0.05)"
                          : "#fff",
                    }}
                  >
                    <CardActionArea
                      component="div"
                      onClick={() => {
                        selectChatSession(session.chat_id);
                        if (isMobile) setIsHistoryOpen(false);
                      }}
                      sx={{ p: 2 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.75,
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            lineHeight: 1.4,
                            maxHeight: "2.8em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            color:
                              currentChatId === session.chat_id
                                ? "#6366f1"
                                : "#1f2937",
                            flex: 1,
                            pr: 1,
                          }}
                        >
                          {session.user_prompt
                            ? truncate(session.user_prompt, 30)
                            : "New Chat"}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              flexShrink: 0,
                              fontSize: "0.7rem",
                              color: "#9ca3af",
                              fontWeight: 500,
                            }}
                          >
                            {formatTime(session.created_at)}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) =>
                              handleSessionMenuOpen(e, session.chat_id)
                            }
                            sx={{
                              p: 0.5,
                              color: "#9ca3af",
                              "&:hover": {
                                bgcolor: "rgba(99, 102, 241, 0.1)",
                                color: "#6366f1",
                              },
                              transition: "all 0.2s",
                            }}
                          >
                            <MoreVert sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: "0.75rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#6b7280",
                          lineHeight: 1.5,
                        }}
                      >
                        {(() => {
                          let content = "...";
                          if (session.llm_response) {
                            if (typeof session.llm_response === "string") {
                              content = session.llm_response;
                            } else if (
                              typeof session.llm_response === "object"
                            ) {
                              content =
                                session.llm_response.llm_response ||
                                "Structured Response";
                            }
                          }
                          return truncate(content, 40);
                        })()}
                      </Typography>
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
          {hasMoreSessions && (
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={loadMoreSessions}
              disabled={isFetchingMoreSessions}
              startIcon={
                isFetchingMoreSessions ? (
                  <CircularProgress size={14} sx={{ color: "#6366f1" }} />
                ) : (
                  <History />
                )
              }
              sx={{
                my: 1.5,
                textTransform: "none",
                color: "#6366f1",
                borderColor: "#e5e7eb",
                borderRadius: 2,
                fontWeight: 500,
                "&:hover": {
                  color: "white",
                  borderColor: "#6366f1",
                  bgcolor: "#6366f1",
                },
                bgcolor: "rgba(99, 102, 241, 0.05)",
              }}
            >
              {isFetchingMoreSessions ? "Loading..." : "Load Older Chats"}
            </Button>
          )}
        </Box>

        <Box
          onClick={() => setIsHistoryOpen(false)}
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.3)",
            backdropFilter: "blur(2px)",
            zIndex: 10,
            opacity: isHistoryOpen ? 1 : 0,
            visibility: isHistoryOpen ? "visible" : "hidden",
            transition: "all 0.3s ease-in-out",
            pointerEvents: isHistoryOpen ? "auto" : "none",
          }}
        />

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            background: "linear-gradient(180deg, #f9fafb 0%, #ffffff 100%)",
            position: "relative",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              opacity: 0.02,
              pointerEvents: "none",
              backgroundImage:
                "radial-gradient(circle at 50% 50%, #6366f1 1px, transparent 1px)",
              backgroundSize: "32px 32px",
            }}
          />

          <Box
            ref={messagesContainerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              px: { xs: 2, md: 4 },
              py: 3,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#d1d5db",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            }}
          >
            {isLoadingMessages ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flex: 1,
                }}
              >
                <CircularProgress size={40} sx={{ color: "#6366f1" }} />
              </Box>
            ) : activeMessages.length === 0 ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    p: 4,
                    background:
                      "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                    borderRadius: "50%",
                    boxShadow: "0 8px 32px rgba(99, 102, 241, 0.3)",
                    mb: 3,
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: -8,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(79, 70, 229, 0.2) 100%)",
                      zIndex: -1,
                    },
                  }}
                >
                  <SmartToy sx={{ fontSize: 56, color: "#fff" }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{ mb: 1.5, fontWeight: 700, color: "#1f2937" }}
                >
                  Start a New Conversation
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#6b7280", fontWeight: 500 }}
                >
                  How can I assist you today?
                </Typography>
              </Box>
            ) : (
              <Stack spacing={0}>
                {hasMoreMessages && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mb: 3 }}
                  >
                    <Button
                      variant="text"
                      size="small"
                      onClick={loadMoreMessages}
                      disabled={isFetchingMoreMessages}
                      startIcon={
                        isFetchingMoreMessages ? (
                          <CircularProgress
                            size={14}
                            sx={{ color: "#6366f1" }}
                          />
                        ) : (
                          <History sx={{ fontSize: 16 }} />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        color: "#6b7280",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        "&:hover": {
                          bgcolor: "rgba(99, 102, 241, 0.08)",
                          color: "#6366f1",
                        },
                      }}
                    >
                      Load Older Messages
                    </Button>
                  </Box>
                )}
                {activeMessages.map((chat, index) => (
                  <ChatMessage
                    key={chat.id}
                    chat={chat}
                    isLatest={index === activeMessages.length - 1}
                  />
                ))}
                <div ref={messagesEndRef} />
              </Stack>
            )}
          </Box>

          <Box
            sx={{
              p: 2,
              background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
              borderTop: "1px solid #e5e7eb",
              boxShadow: "0 -4px 16px rgba(0,0,0,0.04)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                flexWrap: { xs: "wrap", md: "nowrap" },
                gap: 1.5,
                mb: 1.5,
                px: 0.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6366f1",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    opacity: 0.9,
                  }}
                >
                  {mode === "not_outreached"
                    ? "WEB SEARCH"
                    : mode === "internal_details"
                      ? "INTERNAL DETAILS"
                      : "COMPANY PORTFOLIO"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#9ca3af",
                    fontWeight: 500,
                    fontSize: "0.65rem",
                  }}
                >
                  •{" "}
                  {mode === "not_outreached"
                    ? "Fetch and explore companies from web"
                    : mode === "internal_details"
                      ? "Search internal company records and uploaded file names"
                      : "Search and fetch information from existing company data"}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  ml: "auto",
                  width: { xs: "100%", md: "auto" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    minWidth: { xs: 0, md: 170 },
                    visibility:
                      mode === "not_outreached" ? "visible" : "hidden",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#6b7280",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  >
                    Include Web Results
                  </Typography>
                  <Switch
                    size="small"
                    checked={webSearch}
                    onChange={(e) => setWebSearch(e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#10b981",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#10b981",
                        },
                    }}
                  />
                </Box>
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(e, newMode) => {
                    if (newMode !== null) setMode(newMode);
                  }}
                  aria-label="search mode"
                  size="small"
                  sx={{
                    bgcolor: "#f3f4f6",
                    borderRadius: 3,
                    p: 0.5,
                    flexShrink: 0,
                    "& .MuiToggleButton-root": {
                      border: "none",
                      borderRadius: 2,
                      px: 2,
                      py: 0.5,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "#6b7280",
                      textTransform: "none",
                      "&.Mui-selected": {
                        bgcolor: "#fff",
                        color: "#6366f1",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.5)",
                        color: "#4b5563",
                      },
                    },
                  }}
                >
                  <ToggleButton value="not_outreached">
                    Not Outreached
                  </ToggleButton>
                  <ToggleButton value="internal_details">
                    Internal Details
                  </ToggleButton>
                  <ToggleButton value="outreached">Outreached</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
            <Paper
              component="form"
              elevation={0}
              sx={{
                p: "4px 8px",
                display: "flex",
                alignItems: "center",
                borderRadius: 2.5,
                border: "1px solid #e5e7eb",
                bgcolor: "#fff",
                transition: "all 0.2s",
                "&:focus-within": {
                  borderColor: "#6366f1",
                  boxShadow: `0 0 0 3px rgba(99, 102, 241, 0.1)`,
                },
              }}
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
            >
              <InputBase
                ref={inputRef}
                sx={{
                  ml: 2,
                  flex: 1,
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  color: "#1f2937",
                }}
                placeholder="Ask anything..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                disabled={isStreaming}
                autoFocus
              />
              <IconButton
                type="submit"
                disabled={!inputValue.trim() || isStreaming}
                sx={{
                  p: 1,
                  background:
                    !inputValue.trim() || isStreaming
                      ? "#e5e7eb"
                      : "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                  color: "#fff",
                  "&:hover": {
                    background:
                      !inputValue.trim() || isStreaming
                        ? "#e5e7eb"
                        : "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
                  },
                  "&:disabled": {
                    bgcolor: "#e5e7eb",
                    color: "#9ca3af",
                  },
                  transition: "all 0.2s",
                }}
              >
                <Send sx={{ fontSize: 16, transform: "rotate(-45deg)" }} />
              </IconButton>
            </Paper>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 1,
                color: "#9ca3af",
                fontSize: "0.65rem",
                fontWeight: 500,
              }}
            >
              AI can make mistakes. Please verify important information.
            </Typography>
          </Box>
        </Box>

        <Menu
          anchorEl={sessionMenuAnchor}
          open={Boolean(sessionMenuAnchor)}
          onClose={handleSessionMenuClose}
          PaperProps={{
            elevation: 0,
            sx: {
              width: 160,
              mt: 1,
              borderRadius: 2.5,
              border: "1px solid #e5e7eb",
              boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
              overflow: "hidden",
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem
            onClick={handleDeleteSession}
            sx={{
              fontSize: "0.85rem",
              color: "#ef4444",
              py: 1.5,
              px: 2,
              fontWeight: 500,
              "&:hover": {
                bgcolor: "rgba(239, 68, 68, 0.08)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Delete
                fontSize="small"
                sx={{ fontSize: 18, color: "#ef4444" }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Delete Chat"
              primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
            />
          </MenuItem>
        </Menu>
      </Box>
    </Drawer>
  );
}

const ImageTitle = () => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ position: "relative" }}>
        <Avatar
          sx={{
            background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
            width: { xs: 36, sm: 44 },
            height: { xs: 36, sm: 44 },
            boxShadow: "0 4px 16px rgba(99, 102, 241, 0.3)",
            border: "2px solid #fff",
          }}
        >
          <SmartToy sx={{ fontSize: { xs: 20, sm: 26 } }} />
        </Avatar>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 14,
            height: 14,
            bgcolor: "#10b981",
            borderRadius: "50%",
            border: "3px solid #fff",
            zIndex: 1,
            boxShadow: "0 2px 8px rgba(16, 185, 129, 0.4)",
          }}
        />
      </Box>
      <Box>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 800,
            lineHeight: 1.2,
            letterSpacing: "-0.3px",
            fontSize: { xs: "0.9rem", sm: "1.05rem" },
            background: "linear-gradient(135deg, #1f2937 0%, #4b5563 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          CRM AI
        </Typography>
      </Box>
    </Box>
  );
};
